// RESONATE_ANALYTICS_WAVE2
// Advanced analytics layer - depends on /lib/analytics.js (trackEvent).
// All listeners are one-shot per page-load and never throw.
import { trackEvent } from "@/lib/analytics";

const isBrowser = () => typeof window !== "undefined";
const safe = (fn) => { try { fn(); } catch (e) { /* analytics must never break the UI */ } };

/* ---------------- UTM first-touch persistence ----------------
 * Stores the first set of UTM params we ever see for this browser, in localStorage.
 * Then attaches them to every trackEvent call as first_touch_* via a global enricher.
 * Lets you attribute a signup that happens weeks later back to the original campaign.
 */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];
const STORE_KEY = "resonate_first_touch_v1";

const captureFirstTouch = () => {
  if (!isBrowser()) return;
  safe(() => {
    if (localStorage.getItem(STORE_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const found = {};
    let any = false;
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) { found[k] = v; any = true; }
    }
    if (any) {
      found.first_touch_at = new Date().toISOString();
      found.first_touch_landing_path = window.location.pathname;
      found.first_touch_referrer = document.referrer || "(direct)";
      localStorage.setItem(STORE_KEY, JSON.stringify(found));
      trackEvent("first_touch_captured", found);
    }
  });
};

export const getFirstTouch = () => {
  if (!isBrowser()) return {};
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) { return {}; }
};

/* ---------------- Scroll depth (25/50/75/100) ---------------- */
const initScrollDepth = () => {
  if (!isBrowser()) return;
  const fired = new Set();
  const onScroll = () => {
    safe(() => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      if (total <= 0) return;
      const pct = Math.round((h.scrollTop / total) * 100);
      [25, 50, 75, 100].forEach(threshold => {
        if (pct >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          trackEvent("scroll_depth", { percent: threshold, path: window.location.pathname });
        }
      });
      if (fired.size === 4) window.removeEventListener("scroll", onScroll);
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
};

/* ---------------- Engaged time (10s / 30s / 60s / 180s active) ----------------
 * Counts only seconds where the tab is visible AND the user has moved/scrolled/typed
 * in the last 15s. Filters out idle tabs.
 */
const initEngagedTime = () => {
  if (!isBrowser()) return;
  let activeSeconds = 0;
  let lastActivity = Date.now();
  const fired = new Set();
  const milestones = [10, 30, 60, 180];

  const bumpActivity = () => { lastActivity = Date.now(); };
  ["mousemove", "scroll", "keydown", "click", "touchstart"].forEach(e =>
    window.addEventListener(e, bumpActivity, { passive: true })
  );

  const tick = setInterval(() => {
    safe(() => {
      const visible = document.visibilityState === "visible";
      const recentlyActive = Date.now() - lastActivity < 15000;
      if (visible && recentlyActive) {
        activeSeconds += 1;
        milestones.forEach(m => {
          if (activeSeconds >= m && !fired.has(m)) {
            fired.add(m);
            trackEvent("engaged_time", { seconds: m, path: window.location.pathname });
          }
        });
        if (fired.size === milestones.length) clearInterval(tick);
      }
    });
  }, 1000);
};

/* ---------------- Outbound link tracking ----------------
 * Auto-fires for any click on an <a> whose host differs from the current host.
 */
const initOutboundLinks = () => {
  if (!isBrowser()) return;
  document.addEventListener("click", (ev) => {
    safe(() => {
      const a = ev.target && ev.target.closest && ev.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      let url;
      try { url = new URL(href, window.location.href); } catch (e) { return; }
      if (url.host && url.host !== window.location.host) {
        trackEvent("outbound_click", {
          url: url.href,
          host: url.host,
          text: (a.textContent || "").trim().slice(0, 80),
          location: window.location.pathname,
        });
      }
    });
  }, { capture: true, passive: true });
};

/* ---------------- Web Vitals -> GA + PostHog ----------------
 * Inlined minimal LCP/CLS/INP/FCP/TTFB observer (no external dep).
 * Sends one event per metric, with rating buckets matching Google's thresholds.
 */
const initWebVitals = () => {
  if (!isBrowser()) return;
  safe(() => {
    // LCP
    const lcpObs = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) {
        const value = Math.round(last.renderTime || last.loadTime || last.startTime);
        const rating = value <= 2500 ? "good" : value <= 4000 ? "needs_improvement" : "poor";
        trackEvent("web_vital_lcp", { value, rating });
      }
    });
    lcpObs.observe({ type: "largest-contentful-paint", buffered: true });

    // CLS
    let clsValue = 0;
    let clsReported = false;
    const clsObs = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (!e.hadRecentInput) clsValue += e.value;
      }
    });
    clsObs.observe({ type: "layout-shift", buffered: true });
    const reportCls = () => {
      if (clsReported) return;
      clsReported = true;
      const value = Math.round(clsValue * 1000) / 1000;
      const rating = value <= 0.1 ? "good" : value <= 0.25 ? "needs_improvement" : "poor";
      trackEvent("web_vital_cls", { value, rating });
    };
    addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") reportCls(); });
    addEventListener("pagehide", reportCls);

    // INP (approximation via event timings)
    let worstInp = 0;
    const inpObs = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.duration > worstInp) worstInp = e.duration;
      }
    });
    try { inpObs.observe({ type: "event", buffered: true, durationThreshold: 16 }); } catch (e) {}
    addEventListener("pagehide", () => {
      if (worstInp > 0) {
        const value = Math.round(worstInp);
        const rating = value <= 200 ? "good" : value <= 500 ? "needs_improvement" : "poor";
        trackEvent("web_vital_inp", { value, rating });
      }
    });

    // FCP + TTFB from navigation timing
    addEventListener("load", () => {
      safe(() => {
        const nav = performance.getEntriesByType("navigation")[0];
        if (nav) {
          const ttfb = Math.round(nav.responseStart);
          const rating = ttfb <= 800 ? "good" : ttfb <= 1800 ? "needs_improvement" : "poor";
          trackEvent("web_vital_ttfb", { value: ttfb, rating });
        }
        const fcp = performance.getEntriesByName("first-contentful-paint")[0];
        if (fcp) {
          const value = Math.round(fcp.startTime);
          const rating = value <= 1800 ? "good" : value <= 3000 ? "needs_improvement" : "poor";
          trackEvent("web_vital_fcp", { value, rating });
        }
      });
    });
  });
};

/* ---------------- JS error capture -> PostHog ---------------- */
const initErrorCapture = () => {
  if (!isBrowser()) return;
  window.addEventListener("error", (ev) => {
    safe(() => {
      if (window.posthog && typeof window.posthog.captureException === "function") {
        window.posthog.captureException(ev.error || new Error(ev.message), {
          source: "window.error",
          filename: ev.filename,
          lineno: ev.lineno,
        });
      }
      trackEvent("js_error", {
        message: (ev.message || "").slice(0, 200),
        filename: ev.filename,
        lineno: ev.lineno,
      });
    });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    safe(() => {
      const reason = ev.reason;
      if (window.posthog && typeof window.posthog.captureException === "function") {
        window.posthog.captureException(reason instanceof Error ? reason : new Error(String(reason)), {
          source: "unhandledrejection",
        });
      }
      trackEvent("js_unhandled_rejection", {
        reason: String(reason).slice(0, 200),
      });
    });
  });
};

/* ---------------- Exit intent ----------------
 * Desktop only - fires once when mouse leaves through the top of the viewport.
 */
const initExitIntent = () => {
  if (!isBrowser()) return;
  if (window.innerWidth < 1024) return; // skip mobile
  let fired = false;
  const onMouseLeave = (ev) => {
    if (fired) return;
    if (ev.clientY <= 0) {
      fired = true;
      trackEvent("exit_intent", {
        path: window.location.pathname,
        time_on_page_ms: Math.round(performance.now()),
      });
      document.removeEventListener("mouseleave", onMouseLeave);
    }
  };
  document.addEventListener("mouseleave", onMouseLeave);
};

/* ---------------- PostHog identify() bridge ----------------
 * If the app sets window.__resonate_user = { id, email, ... } at any point,
 * pass it to PostHog so anonymous -> identified session is preserved.
 * Works for the cross-domain handoff when a user signs up at app.getresonate.ai
 * (the app posts a window.postMessage back to the landing tab, or sets a cookie).
 */
const initIdentifyBridge = () => {
  if (!isBrowser()) return;
  const tryIdentify = () => {
    safe(() => {
      const u = window.__resonate_user;
      if (!u || !u.id) return;
      if (window.posthog && typeof window.posthog.identify === "function") {
        window.posthog.identify(u.id, { email: u.email, plan: u.plan, role: u.role });
        if (typeof window.gtag === "function") {
          window.gtag("set", { user_id: u.id });
        }
        delete window.__resonate_user; // identify once
      }
    });
  };
  // Check on init + watch for postMessage from the app subdomain
  tryIdentify();
  window.addEventListener("message", (ev) => {
    safe(() => {
      if (!ev.data || ev.data.type !== "resonate:identify") return;
      window.__resonate_user = ev.data.user;
      tryIdentify();
    });
  });
};

/* ---------------- Sticky bar impression (visible >=1s) ---------------- */
const initStickyBarImpression = () => {
  if (!isBrowser()) return;
  let fired = false;
  const check = setInterval(() => {
    safe(() => {
      if (fired) return clearInterval(check);
      const el = document.querySelector("[data-testid='sticky-bar']");
      if (el && el.offsetParent !== null) {
        setTimeout(() => {
          if (!fired && el.offsetParent !== null) {
            fired = true;
            trackEvent("sticky_bar_impression");
            clearInterval(check);
          }
        }, 1000);
      }
    });
  }, 500);
  setTimeout(() => clearInterval(check), 30000); // give up after 30s
};

/* ---------------- Public entrypoint ---------------- */
export const bootstrapAnalytics = () => {
  if (!isBrowser()) return;
  if (window.__resonateAnalyticsBooted) return;
  window.__resonateAnalyticsBooted = true;

  captureFirstTouch();
  initScrollDepth();
  initEngagedTime();
  initOutboundLinks();
  initWebVitals();
  initErrorCapture();
  initExitIntent();
  initIdentifyBridge();
  initStickyBarImpression();
};
